import React, {FC, useEffect, useState, useMemo} from 'react';
import styled from "styled-components";
import Modal from "antd/es/modal/Modal";
import {Tooltip} from "antd";
import {defineMessages, useIntl} from "react-intl";
import {useAppSelector} from "../../../../store/store";
import RouteArrowIcon from "../../../../media/icons/route_arrow.svg?react";
import InfoIcon from "../../../../media/icons/info.svg?react";
import PaxIcon from "../../../../media/icons/pax.svg?react";
import FuelIcon from "../../../../media/icons/fuel.svg?react";
import CloseIcon from "../../../../media/icons/close.svg?react";
import WarningIcon from "../../../../media/icons/warning.svg?react";
import {AcceptButton} from "../../../../components/UI/Buttons/AcceptButton";
import {DeclineButton} from "../../../../components/UI/Buttons/DeclineButton";
import {DisplayAirport, DisplayRoute} from "../../../../utils/helpers/textDisplayHelpers/DisplayAirport";
import {formatDate, parseStringAsUTCDate} from "../../../../utils/helpers/dateHelpers/dateHelpers";
import {calculateArrivalDate} from "../../../../utils/helpers/dateHelpers/calculateArrivalDate";
import {DisplayFlyPrice} from "../../../../utils/helpers/textDisplayHelpers/DisplayFlyPrice";
import {IAirport, ICurrency} from "../../../../store/reducers/fleetReducer/fleetReducerTypes";
import {mapAirportEntityToIAirport} from "../../../../store/reducers/fleetReducer/fleetMappers";
import {useCurrencyConverter} from "../../../../hooks/useCurrencyConverter";
import {getBaseCurrencyId, getBaseCurrencyInfo} from "../../../../utils/helpers/currencyConverter/currencyConverter";
import {Loader} from "../../../../components/UI/Loader/Loader";
import {NoData} from "../../../../components/UI/NoData/NoData";
import {API} from "../../../../API/API";
import {BookingApiOrderListRequest, BookingApiOrderRequestListRequest, OrderEntity, OrderRequestEntity, OrderRouteEntity, PageModel} from "../../../../proto/generated/api_entities_pb";
import {OrderRequestStatusEnum, OrderRequestPlaneStatusEnum} from "../../../../utils/constans/statuses/orderRequestStatuses";
import {OrderStatusEnum} from "../../../../store/reducers/chartererRequestsReducer/chartererRequestsReducerTypes";
import {Logger} from "../../../../utils/logger/Logger";

const PastResponsesModalMessages = defineMessages({
  title: {
    id: "pastResponsesModal.title",
    defaultMessage: "Прошлые ответы по рейсу",
  },
  plane: {
    id: "pastResponsesModal.plane",
    defaultMessage: "Самолет",
  },
  routeDate: {
    id: "pastResponsesModal.routeDate",
    defaultMessage: "Дата маршрута",
  },
  price: {
    id: "pastResponsesModal.price",
    defaultMessage: "Цена",
  },
  passengers: {
    id: "pastResponsesModal.passengers",
    defaultMessage: "Пассажиры",
  },
  refuelings: {
    id: "pastResponsesModal.refuelings",
    defaultMessage: "Дозаправки",
  },
  noSimilarRequests: {
    id: "pastResponsesModal.noSimilarRequests",
    defaultMessage: "No similar requests found",
  },
  noPlanesInSimilarOrders: {
    id: "pastResponsesModal.noPlanesInSimilarOrders",
    defaultMessage: "{count, plural, =0 {No similar requests found} =1 {1 similar request found, but it has no aircraft offers yet} other {# similar requests found, but they have no aircraft offers yet}}",
  },
  pendingResponse: {
    id: "pastResponsesModal.pendingResponse",
    defaultMessage: "Requested calculated price, please await operator's response",
  },
  priceConversionTooltip: {
    id: "pastResponsesModal.priceConversionTooltip",
    defaultMessage: "Price converted to your currency",
  },
  orderStatusNotAnswered: {
    id: "pastResponsesModal.orderStatus.notAnswered",
    defaultMessage: "Order: No offers",
  },
  orderStatusAccepted: {
    id: "pastResponsesModal.orderStatus.accepted",
    defaultMessage: "Order: Accepted",
  },
  orderStatusDeclined: {
    id: "pastResponsesModal.orderStatus.declined",
    defaultMessage: "Order: Declined",
  },
});

interface IPastResponse {
  id: number;
  planeRegistrationNumber: string;
  planeId?: number; // For matching planes across similar orders
  planeTypeName?: string; // Aircraft model name
  planeCategoryName?: string; // Aircraft class name
  routeDateFrom: Date;
  routeDateTo: Date;
  price: number;
  currency: ICurrency | null;
  orderRequestCurrency?: ICurrency | null; // Currency from order request (row level), used for secondary price display
  priceInUserCurrency?: number | null;
  userCurrency?: ICurrency | null;
  passengers: number;
  refuelings: number;
  isAccepted: boolean;
  isDeclined: boolean;
  isPending: boolean; // True when operator hasn't responded yet (only preliminaryPrice available)
  similarOrderId?: number; // ID of the similar order this response came from
  similarOrderStatusId?: number; // Order status of the similar order (OrderStatusEnum)
  positioningDurationMinutes?: number; // Positioning flight duration in minutes
  positioningAirportCode?: string; // Airport code for positioning (ICAO or IATA)
}

interface IPastResponsesModal {
  visible: boolean;
  onClose: () => void;
  routeFrom?: IAirport;
  routeTo?: IAirport;
  allRouteAirports?: IAirport[]; // Full route airports for multileg/round trip routes
  orderId?: number;
  orderRequestId?: number;
}

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    padding: 0;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
  }

  .ant-modal-body {
    padding: 0;
    overflow-y: auto;
    flex: 1;
  }

  .ant-modal-close {
    display: none;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 32px 12px 32px;
  border-bottom: 1px solid #DDDDDC;
`;

const HeaderLeft = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const HeaderTitle = styled.div`
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 22px;
  color: #000;
`;

const RouteInfo = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const RouteText = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  color: #000;
  letter-spacing: -0.14px;
  white-space: nowrap;
`;

const RouteArrowIconStyled = styled(RouteArrowIcon)`
  width: 9px;
  height: 11px;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  
  svg {
    width: 16px;
    height: 16px;
    
    path {
      fill: #8C8C8B;
    }
  }
  
  &:hover {
    opacity: 0.7;
  }
`;

const ModalContent = styled.div`
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-height: calc(80vh - 60px);
  overflow-y: auto;
`;

const ResponsesContainer = styled.div`
  border: 1px solid #D9D9D9;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ResponseCard = styled.div<{ $isLast?: boolean }>`
  display: flex;
  gap: 16px;
  padding: 16px;
  align-items: stretch;
  ${({$isLast}) => !$isLast && "border-bottom: 1px solid #D9D9D9;"}
`;

const ResponseCell = styled.div<{ $width?: number; $minWidth?: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 auto;
  min-width: ${({$minWidth}) => $minWidth ? `${$minWidth}px` : '0'};
  ${({$width}) => $width ? `flex: 0 0 ${$width}px;` : ""}
`;

const CellLabel = styled.div`
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.2;
  color: #808080;
`;

const CellContent = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 1;
  padding: 4px 0;
`;

const CellValue = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  color: #000;
  letter-spacing: -0.14px;
`;

const PriceCell = styled(ResponseCell)`
  flex: 1 1 auto;
  min-width: 80px;
`;

const PriceContent = styled.div`
  display: flex;
  gap: 2px;
  align-items: flex-start;
`;

const PriceValue = styled.div`
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: #000;
  letter-spacing: -0.14px;
  white-space: nowrap;
  align-self: flex-start;
`;

const PriceInUserCurrency = styled.div`
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #666;
  margin-top: 4px;
`;

const PendingIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  cursor: help;
  padding: 1px;
  background-color: #FFF9E6;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  
  svg {
    width: 16px;
    height: 16px;
    
    path {
      fill: #F4B237;
      stroke: #F4B237;
      stroke-width: 0.3;
    }
  }
  
  &:hover {
    background-color: #FFF3CD;
  }
`;

const InfoIconWrapper = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const IconValueWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const PassengerIconWrapper = styled.div`
  width: 17px;
  height: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 17px;
    height: 17px;
    
    path {
      fill: #808080;
    }
  }
`;

const FuelIconWrapper = styled.div`
  width: 15px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 15px;
    height: 15px;
  }
`;

const RefuelValue = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.2;
  color: #1890FF;
  letter-spacing: -0.14px;
  white-space: nowrap;
`;

const ActionsCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
  margin-left: auto;
`;

const ActionsButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const OrderStatusLabel = styled.div`
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.2;
  color: #808080;
`;


const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  min-height: 200px;
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  min-height: 200px;
`;

export const PastResponsesModal: FC<IPastResponsesModal> = ({
  visible,
  onClose,
  routeFrom,
  routeTo,
  allRouteAirports,
  orderId,
  orderRequestId,
}) => {
  const intl = useIntl();
  const {offset} = useAppSelector(state => state.user.oneItemObject.item.timezone) || {offset: 0};
  const userCurrency = useAppSelector(state => state.user.oneItemObject.item?.currency || state.company.oneItemObject.item?.currency);
  const {convertSync, isReady} = useCurrencyConverter();
  const baseCurrencyId = getBaseCurrencyId();
  const baseCurrency = getBaseCurrencyInfo();

  // Fallback currency when response has no currency (ensures all prices display with a currency)
  const getPriceDisplayCurrency = (response: IPastResponse): ICurrency | null =>
    response.currency ?? (baseCurrency ? { id: baseCurrency.id, name: baseCurrency.name } : null) ?? userCurrency ?? null;

  // Sort: accepted orders first, then waiting, then declined; same for plane status; most recent within each group on top
  const sortPastResponses = (responses: IPastResponse[]): IPastResponse[] => {
    const orderStatusRank = (statusId?: number) => {
      if (statusId === OrderStatusEnum.ACCEPTED) return 0;
      if (statusId === OrderStatusEnum.NOT_ANSWERED) return 1;
      if (statusId === OrderStatusEnum.DECLINED) return 2;
      return 1;
    };
    const planeStatusRank = (r: IPastResponse) => {
      if (r.isAccepted) return 0;
      if (r.isPending) return 1;
      if (r.isDeclined) return 2;
      return 1;
    };
    return [...responses].sort((a, b) => {
      const orderDiff = orderStatusRank(a.similarOrderStatusId) - orderStatusRank(b.similarOrderStatusId);
      if (orderDiff !== 0) return orderDiff;
      const planeDiff = planeStatusRank(a) - planeStatusRank(b);
      if (planeDiff !== 0) return planeDiff;
      return b.routeDateFrom.getTime() - a.routeDateFrom.getTime(); // most recent first
    });
  };
  const [responses, setResponses] = useState<IPastResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fullRouteAirports, setFullRouteAirports] = useState<IAirport[]>([]);
  const [similarOrdersCount, setSimilarOrdersCount] = useState<number>(0);

  // Build full route display from all airports
  const routeDisplay = useMemo(() => {
    // Use allRouteAirports prop if provided (from current order)
    if (allRouteAirports && allRouteAirports.length > 0) {
      return DisplayRoute(allRouteAirports, "codes");
    }
    // Use fullRouteAirports from similar orders if available
    if (fullRouteAirports.length > 0) {
      return DisplayRoute(fullRouteAirports, "codes");
    }
    // Fallback to simple route if full route not available
    const routeFromDisplay = routeFrom ? DisplayAirport(routeFrom, "codes") : "";
    const routeToDisplay = routeTo ? DisplayAirport(routeTo, "codes") : "";
    if (routeFromDisplay && routeToDisplay) {
      return `${routeFromDisplay} → ${routeToDisplay}`;
    }
    return "";
  }, [allRouteAirports, fullRouteAirports, routeFrom, routeTo]);

  // Загружаем данные при открытии модалки
  useEffect(() => {
    if (visible && (orderId || orderRequestId)) {
      loadSimilarList();
    } else if (!visible) {
      // Очищаем данные при закрытии
      setResponses([]);
      setIsLoading(false);
      setSimilarOrdersCount(0);
    }
  }, [visible, orderId, orderRequestId]);

  const loadSimilarList = async () => {
    setIsLoading(true);
    setResponses([]);
    setSimilarOrdersCount(0);

    try {
      if (orderRequestId) {
        // Запрос для OrderRequest
        const req = new BookingApiOrderRequestListRequest()
          .setOrderrequestid(orderRequestId)
          .setPagemodel(new PageModel().setPage(0).setLimit(50))
          .setIsshowarchived(false);
        
        const res = await API.orderRequestSimilarList(req);
        const items = res.getOrderrequestsList();
        
        // Собираем все самолеты из planeList каждого orderRequest
        const mappedResponses = items
          .flatMap((item: OrderRequestEntity) => {
            const orderRouteOrderRequests = item.getOrderrouteorderrequestsList();
            const order = item.getOrder();
            const orderRoutes = order?.getOrderroutesList() || [];
            
            // Проверяем наличие маршрутов
            let hasValidRoutes = false;
            let firstRoute: OrderRouteEntity | undefined;
            let lastRoute: OrderRouteEntity | undefined;
            let routeDateFrom: Date | undefined;
            let routeDateTo: Date | undefined;
            let passengers: number | undefined;
            
            if (orderRouteOrderRequests.length > 0) {
              const firstRouteRequest = orderRouteOrderRequests[0];
              const lastRouteRequest = orderRouteOrderRequests[orderRouteOrderRequests.length - 1];
              firstRoute = firstRouteRequest.getOrderroute();
              lastRoute = lastRouteRequest.getOrderroute();
              
              if (firstRoute && lastRoute && firstRoute.getAirportfrom() && lastRoute.getAirportto()) {
                hasValidRoutes = true;
                const airportFrom = firstRoute.getAirportfrom()!;
                const airportTo = lastRoute.getAirportto()!;
                const airportFromMapped = mapAirportEntityToIAirport(airportFrom);
                const airportToMapped = mapAirportEntityToIAirport(airportTo);
                
                routeDateFrom = parseStringAsUTCDate(firstRoute.getDeparturedate());
                routeDateTo = calculateArrivalDate(
                  airportFromMapped,
                  airportToMapped,
                  parseStringAsUTCDate(lastRoute.getDeparturedate()),
                  lastRouteRequest.getFlyduration() + (lastRouteRequest.getTechnicalstopduration() || 0)
                );
                
                passengers = Math.max(...orderRouteOrderRequests.map(r => {
                  const route = r.getOrderroute();
                  return route ? route.getPax() : 0;
                }), 0);
              }
            } else if (orderRoutes.length > 0) {
              firstRoute = orderRoutes[0];
              lastRoute = orderRoutes[orderRoutes.length - 1];
              
              if (firstRoute && lastRoute && firstRoute.getAirportfrom() && lastRoute.getAirportto()) {
                hasValidRoutes = true;
                const airportFrom = firstRoute.getAirportfrom()!;
                const airportTo = lastRoute.getAirportto()!;
                const airportFromMapped = mapAirportEntityToIAirport(airportFrom);
                const airportToMapped = mapAirportEntityToIAirport(airportTo);
                
                routeDateFrom = parseStringAsUTCDate(firstRoute.getDeparturedate());
                routeDateTo = parseStringAsUTCDate(lastRoute.getDeparturedate());
                
                passengers = Math.max(...orderRoutes.map(r => r.getPax()), 0);
              }
            }
            
            if (!hasValidRoutes) {
              return [];
            }
            
            // Берем все самолеты из planeList
            const planesList = item.getPlanesList();
            
            // Если нет самолетов, создаем запись на основе orderRequest (запрошенная цена)
            if (planesList.length === 0) {
              const orderRequestPrice = item.getPrice() || 0;
              const orderRequestCurrency = item.getCurrency();
              
              if (orderRequestPrice > 0 && orderRequestCurrency) {
                // Конвертируем цену в валюту пользователя, если нужно
                let priceInUserCurrency: number | null = null;
                let userCurrencyObj: ICurrency | null = null;
                
                if (userCurrency && isReady && baseCurrencyId) {
                  const fromCurrencyId = orderRequestCurrency.getCurrencyid();
                  const toCurrencyId = userCurrency.id;
                  
                  if (fromCurrencyId !== toCurrencyId) {
                    try {
                      priceInUserCurrency = convertSync(orderRequestPrice, fromCurrencyId, toCurrencyId);
                      priceInUserCurrency = Math.round(priceInUserCurrency * 100) / 100;
                      userCurrencyObj = userCurrency;
                    } catch (error) {
                      // Если конвертация не удалась, оставляем null
                    }
                  } else {
                    // Same currency: show price with user's currency for consistent display
                    priceInUserCurrency = Math.round(orderRequestPrice * 100) / 100;
                    userCurrencyObj = userCurrency;
                  }
                }
                
                return [{
                  id: item.getOrderrequestid(),
                  planeRegistrationNumber: "N/A", // No plane assigned yet
                  routeDateFrom: routeDateFrom!,
                  routeDateTo: routeDateTo!,
                  price: orderRequestPrice,
                  currency: {
                    id: orderRequestCurrency.getCurrencyid(),
                    name: orderRequestCurrency.getName(),
                  },
                  orderRequestCurrency: {
                    id: orderRequestCurrency.getCurrencyid(),
                    name: orderRequestCurrency.getName(),
                  },
                  priceInUserCurrency,
                  userCurrency: userCurrencyObj,
                  passengers: passengers || 0,
                  refuelings: 0,
                  isAccepted: false,
                  isDeclined: false,
                  isPending: true, // No operator response yet
                  similarOrderStatusId: order?.getOrderstatusid?.() || undefined,
                }];
              }
              
              return [];
            }
            
            // Для каждого самолета создаем отдельную запись
            return planesList.map((planeEntity) => {
              const plane = planeEntity.getPlane();
              if (!plane || plane.getPlaneid() === 0) {
                return null;
              }
              
              // Определяем, есть ли ответ от оператора
              const operatorPriceRaw = planeEntity.getOperatorprice();
              const preliminaryPriceRaw = planeEntity.getPreliminaryprice();
              
              // Конвертируем в числа
              const operatorPrice = operatorPriceRaw ? Number(operatorPriceRaw) : 0;
              const preliminaryPrice = preliminaryPriceRaw ? Number(preliminaryPriceRaw) : 0;
              
              const isPending = !operatorPrice || operatorPrice === 0;
              
              // Используем operatorPrice если есть, иначе preliminaryPrice (запрошенная цена)
              const price = operatorPrice || preliminaryPrice;
              
              // Используем operatorCurrency если есть, иначе currency из plane, иначе orderRequest currency
              const operatorCurrency = planeEntity.getOperatorcurrency();
              const currency = planeEntity.getCurrency();
              const orderRequestCurrency = item.getCurrency();
              const finalCurrency = operatorCurrency || currency || orderRequestCurrency;
              
              // Конвертируем цену в валюту пользователя, если нужно
              let priceInUserCurrency: number | null = null;
              let userCurrencyObj: ICurrency | null = null;
              
              if (userCurrency && isReady && baseCurrencyId && price > 0) {
                // Для preliminaryPrice: currency из plane или orderRequest или базовая
                // Для operatorPrice: operatorCurrency или currency из plane или orderRequest
                const fromCurrencyId = isPending 
                  ? (currency?.getCurrencyid() || orderRequestCurrency?.getCurrencyid() || baseCurrencyId)
                  : (operatorCurrency?.getCurrencyid() || currency?.getCurrencyid() || orderRequestCurrency?.getCurrencyid() || baseCurrencyId);
                const toCurrencyId = userCurrency.id;
                
                if (fromCurrencyId !== toCurrencyId) {
                  try {
                    priceInUserCurrency = convertSync(price, fromCurrencyId, toCurrencyId);
                    priceInUserCurrency = Math.round(priceInUserCurrency * 100) / 100;
                    userCurrencyObj = userCurrency;
                  } catch (error) {
                    // Если конвертация не удалась, оставляем null
                  }
                } else {
                  // Same currency: show price with user's currency for consistent display
                  priceInUserCurrency = Math.round(price * 100) / 100;
                  userCurrencyObj = userCurrency;
                }
              }
              
              // Статус из orderRequestStatusId самолета
              const planeStatusId = planeEntity.getOrderrequeststatusid();
              const isAccepted = planeStatusId === OrderRequestPlaneStatusEnum.STATUS_APPROVED_BY_BROKER;
              const isDeclined = planeStatusId === OrderRequestPlaneStatusEnum.STATUS_DECLINED_BY_OPERATOR || 
                                planeStatusId === OrderRequestPlaneStatusEnum.STATUS_DECLINED_BY_BROKER;
              
              // Дозаправки из refuelingCount
              const refuelings = planeEntity.getRefuelingcount() || 0;
              
              // Positioning information
              // Note: OrderRequestPlaneEntity doesn't have positioningFromAirportId,
              // so we can't determine the positioning airport reliably
              const positioningDurationMinutes = planeEntity.getPositioningdurationminutes();
              // Don't set positioningAirportCode since we don't have positioningFromAirportId in OrderRequestPlaneEntity
              const positioningAirportCode: string | undefined = undefined;
              
              // Если есть flightDurationMinutes, пересчитываем routeDateTo
              let finalRouteDateTo = routeDateTo || routeDateFrom!;
              if (planeEntity.getFlightdurationminutes() && routeDateFrom) {
                const airportFrom = firstRoute!.getAirportfrom()!;
                const airportTo = lastRoute!.getAirportto()!;
                const airportFromMapped = mapAirportEntityToIAirport(airportFrom);
                const airportToMapped = mapAirportEntityToIAirport(airportTo);
                finalRouteDateTo = calculateArrivalDate(
                  airportFromMapped,
                  airportToMapped,
                  routeDateFrom,
                  planeEntity.getFlightdurationminutes()
                );
              }
              
              return {
                id: planeEntity.getOrderrequestplaneid(), // Используем orderRequestPlaneId как уникальный ID
                planeRegistrationNumber: plane.getRegistrationnumber(),
                planeId: plane.getPlaneid(),
                routeDateFrom: routeDateFrom!,
                routeDateTo: finalRouteDateTo,
                price,
                currency: finalCurrency ? {
                  id: finalCurrency.getCurrencyid(),
                  name: finalCurrency.getName(),
                } : null,
                orderRequestCurrency: orderRequestCurrency ? {
                  id: orderRequestCurrency.getCurrencyid(),
                  name: orderRequestCurrency.getName(),
                } : null,
                priceInUserCurrency,
                userCurrency: userCurrencyObj,
                passengers: passengers || 0,
                refuelings,
                isAccepted,
                isDeclined,
                isPending,
                similarOrderStatusId: order?.getOrderstatusid?.() || undefined,
                positioningDurationMinutes: positioningDurationMinutes && positioningDurationMinutes > 0 ? positioningDurationMinutes : undefined,
                positioningAirportCode,
              };
            }).filter(Boolean) as IPastResponse[];
          })
          .flat();
        
        setResponses(sortPastResponses(mappedResponses));
      } else if (orderId) {
        // Запрос для Order
        const req = new BookingApiOrderListRequest()
          .setOrderid(orderId)
          .setPagemodel(new PageModel().setPage(0).setLimit(50))
          .setIsshowarchived(false);
        
        const res = await API.orderSimilarList(req);
        
        // Проверяем ResultResponse
        if (res.getResultresponse() && !res.getResultresponse()?.getIssuccessful()) {
          const errorMessage = res.getResultresponse()?.getErrormessage();
          Logger.error(`Failed to load similar orders: ${errorMessage}`);
          setResponses([]);
          setIsLoading(false);
          return;
        }
        
        const orders = res.getOrdersList();
        
        setSimilarOrdersCount(orders.length);
        
        // Build full route from first order's orderRoutes for display (if not already set from props)
        if (orders.length > 0 && fullRouteAirports.length === 0) {
          const firstOrder = orders[0];
          const orderRoutes = firstOrder.getOrderroutesList() || [];
          if (orderRoutes.length > 0) {
            const routeAirports: IAirport[] = [];
            // Add first airport from first route
            const firstRoute = orderRoutes[0];
            if (firstRoute.getAirportfrom()) {
              routeAirports.push(mapAirportEntityToIAirport(firstRoute.getAirportfrom()!));
            }
            // Add destination airports from all routes
            orderRoutes.forEach(route => {
              if (route.getAirportto()) {
                routeAirports.push(mapAirportEntityToIAirport(route.getAirportto()!));
              }
            });
            setFullRouteAirports(routeAirports);
          }
        }
        
        // Собираем все самолеты из planeList каждого orderRequest
        // Группируем по planeId для ясности - показываем все ответы для каждого самолета
        const allResponses: IPastResponse[] = [];
        
        orders.forEach((orderEntity: OrderEntity) => {
          const orderRequests = orderEntity.getOrderrequestsList();
          const orderRoutes = orderEntity.getOrderroutesList() || [];
          
          orderRequests.forEach((orderRequest) => {
            const orderRouteOrderRequests = orderRequest.getOrderrouteorderrequestsList();
            
            // Проверяем наличие маршрутов
            // Сначала пытаемся получить из orderRouteOrderRequests, если нет - используем orderRoutes из orderEntity
            let hasValidRoutes = false;
            let firstRoute: OrderRouteEntity | undefined;
            let lastRoute: OrderRouteEntity | undefined;
            let routeDateFrom: Date | undefined;
            let routeDateTo: Date | undefined;
            let passengers: number | undefined;
            
            if (orderRouteOrderRequests.length > 0) {
              // Используем маршруты из orderRouteOrderRequests (более точные данные)
              const firstRouteRequest = orderRouteOrderRequests[0];
              const lastRouteRequest = orderRouteOrderRequests[orderRouteOrderRequests.length - 1];
              firstRoute = firstRouteRequest.getOrderroute();
              lastRoute = lastRouteRequest.getOrderroute();
              
              if (firstRoute && lastRoute && firstRoute.getAirportfrom() && lastRoute.getAirportto()) {
                hasValidRoutes = true;
                const airportFrom = firstRoute.getAirportfrom()!;
                const airportTo = lastRoute.getAirportto()!;
                const airportFromMapped = mapAirportEntityToIAirport(airportFrom);
                const airportToMapped = mapAirportEntityToIAirport(airportTo);
                
                routeDateFrom = parseStringAsUTCDate(firstRoute.getDeparturedate());
                routeDateTo = calculateArrivalDate(
                  airportFromMapped,
                  airportToMapped,
                  parseStringAsUTCDate(lastRoute.getDeparturedate()),
                  lastRouteRequest.getFlyduration() + (lastRouteRequest.getTechnicalstopduration() || 0)
                );
                
                passengers = Math.max(...orderRouteOrderRequests.map(r => {
                  const route = r.getOrderroute();
                  return route ? route.getPax() : 0;
                }), 0);
              }
            } else if (orderRoutes.length > 0) {
              // Fallback: используем маршруты из orderEntity
              firstRoute = orderRoutes[0];
              lastRoute = orderRoutes[orderRoutes.length - 1];
              
              if (firstRoute && lastRoute && firstRoute.getAirportfrom() && lastRoute.getAirportto()) {
                hasValidRoutes = true;
                const airportFrom = firstRoute.getAirportfrom()!;
                const airportTo = lastRoute.getAirportto()!;
                const airportFromMapped = mapAirportEntityToIAirport(airportFrom);
                const airportToMapped = mapAirportEntityToIAirport(airportTo);
                
                routeDateFrom = parseStringAsUTCDate(firstRoute.getDeparturedate());
                // Для orderRoutes нет flyDuration, используем только departure date
                const lastRouteDepartureDate = parseStringAsUTCDate(lastRoute.getDeparturedate());
                routeDateTo = calculateArrivalDate(
                  airportFromMapped,
                  airportToMapped,
                  lastRouteDepartureDate,
                  0 // Нет данных о длительности полета, используем 0
                );
                
                passengers = Math.max(...orderRoutes.map(r => r.getPax()), 0);
              }
            }
            
            if (!hasValidRoutes) {
              return;
            }
            
            // Берем все самолеты из planeList
            const planesList = orderRequest.getPlanesList();
            
            // Если нет самолетов, создаем запись на основе orderRequest (запрошенная цена)
            if (planesList.length === 0) {
              const orderRequestPrice = orderRequest.getPrice() || 0;
              const orderRequestCurrency = orderRequest.getCurrency();
              
              if (orderRequestPrice > 0 && orderRequestCurrency) {
                // Конвертируем цену в валюту пользователя, если нужно
                let priceInUserCurrency: number | null = null;
                let userCurrencyObj: ICurrency | null = null;
                
                if (userCurrency && isReady && baseCurrencyId) {
                  const fromCurrencyId = orderRequestCurrency.getCurrencyid();
                  const toCurrencyId = userCurrency.id;
                  
                  if (fromCurrencyId !== toCurrencyId) {
                    try {
                      priceInUserCurrency = convertSync(orderRequestPrice, fromCurrencyId, toCurrencyId);
                      priceInUserCurrency = Math.round(priceInUserCurrency * 100) / 100;
                      userCurrencyObj = userCurrency;
                    } catch (error) {
                      // Если конвертация не удалась, оставляем null
                    }
                  } else {
                    // Same currency: show price with user's currency for consistent display
                    priceInUserCurrency = Math.round(orderRequestPrice * 100) / 100;
                    userCurrencyObj = userCurrency;
                  }
                }
                
                allResponses.push({
                  id: orderRequest.getOrderrequestid(),
                  planeRegistrationNumber: "N/A", // No plane assigned yet
                  routeDateFrom: routeDateFrom!,
                  routeDateTo: routeDateTo!,
                  price: orderRequestPrice,
                  currency: {
                    id: orderRequestCurrency.getCurrencyid(),
                    name: orderRequestCurrency.getName(),
                  },
                  orderRequestCurrency: {
                    id: orderRequestCurrency.getCurrencyid(),
                    name: orderRequestCurrency.getName(),
                  },
                  priceInUserCurrency,
                  userCurrency: userCurrencyObj,
                  passengers: passengers || 0,
                  refuelings: 0,
                  isAccepted: false,
                  isDeclined: false,
                  isPending: true, // No operator response yet
                  similarOrderId: orderEntity.getOrderid(),
                });
              }
              
              return;
            }
            
            // Для каждого самолета создаем отдельную запись
            planesList.forEach((planeEntity) => {
              const plane = planeEntity.getPlane();
              if (!plane || plane.getPlaneid() === 0) {
                return;
              }
              
              // Определяем, есть ли ответ от оператора
              const operatorPriceRaw = planeEntity.getOperatorprice();
              const preliminaryPriceRaw = planeEntity.getPreliminaryprice();
              
              // Конвертируем в числа
              const operatorPrice = operatorPriceRaw ? Number(operatorPriceRaw) : 0;
              const preliminaryPrice = preliminaryPriceRaw ? Number(preliminaryPriceRaw) : 0;
              
              const isPending = !operatorPrice || operatorPrice === 0;
              
              // Используем operatorPrice если есть, иначе preliminaryPrice (запрошенная цена)
              const price = operatorPrice || preliminaryPrice;
              
              // Используем operatorCurrency если есть ответ от оператора, иначе currency из planeEntity
              // Для preliminaryPrice (когда нет ответа от оператора) используем currency из planeEntity
              const operatorCurrency = planeEntity.getOperatorcurrency();
              const currency = planeEntity.getCurrency(); // Currency from plane entity (available even when no operator response)
              // Fallback to orderRequest currency if plane currency is not available
              const orderRequestCurrencyRaw = orderRequest.getCurrency();
              
              // Determine final currency with proper type handling
              // Priority: operatorCurrency (if operator responded) > currency (from planeEntity) > orderRequestCurrencyRaw
              let finalCurrency: typeof operatorCurrency | typeof currency | typeof orderRequestCurrencyRaw | undefined;
              if (operatorCurrency) {
                // Operator has responded, use operator's currency
                finalCurrency = operatorCurrency;
              } else if (currency) {
                // No operator response yet, use currency from planeEntity (this is the requested price currency)
                finalCurrency = currency;
              } else if (orderRequestCurrencyRaw) {
                // Fallback to orderRequest currency
                finalCurrency = orderRequestCurrencyRaw;
              }
              
              // Конвертируем цену в валюту пользователя, если нужно
              let priceInUserCurrency: number | null = null;
              let userCurrencyObj: ICurrency | null = null;
              
              if (userCurrency && isReady && baseCurrencyId && price > 0) {
                // Определяем валюту исходной цены
                // Приоритет: operatorCurrency (если есть ответ) > currency из planeEntity > orderRequestCurrency > baseCurrency
                const fromCurrencyId = isPending 
                  ? (currency?.getCurrencyid() || orderRequestCurrencyRaw?.getCurrencyid() || baseCurrencyId)
                  : (operatorCurrency?.getCurrencyid() || currency?.getCurrencyid() || orderRequestCurrencyRaw?.getCurrencyid() || baseCurrencyId);
                const toCurrencyId = userCurrency.id;
                
                // Конвертируем только если валюта исходной цены отличается от валюты пользователя
                // И если fromCurrencyId валиден (больше 0)
                if (fromCurrencyId && toCurrencyId && fromCurrencyId !== toCurrencyId && fromCurrencyId > 0) {
                  try {
                    priceInUserCurrency = convertSync(price, fromCurrencyId, toCurrencyId);
                    priceInUserCurrency = Math.round(priceInUserCurrency * 100) / 100;
                    userCurrencyObj = userCurrency;
                  } catch (error) {
                    // Если конвертация не удалась, оставляем null
                  }
                } else if (fromCurrencyId === toCurrencyId) {
                  // Same currency: show price with user's currency for consistent display
                  priceInUserCurrency = Math.round(price * 100) / 100;
                  userCurrencyObj = userCurrency;
                }
              }
              
              // Статус из orderRequestStatusId самолета
              const planeStatusId = planeEntity.getOrderrequeststatusid();
              const isAccepted = planeStatusId === OrderRequestPlaneStatusEnum.STATUS_APPROVED_BY_BROKER;
              const isDeclined = planeStatusId === OrderRequestPlaneStatusEnum.STATUS_DECLINED_BY_OPERATOR || 
                                planeStatusId === OrderRequestPlaneStatusEnum.STATUS_DECLINED_BY_BROKER;
              
              // Дозаправки из refuelingCount
              const refuelings = planeEntity.getRefuelingcount() || 0;
              
              // Positioning information
              // Note: OrderRequestPlaneEntity doesn't have positioningFromAirportId,
              // so we can't determine the positioning airport reliably
              const positioningDurationMinutes = planeEntity.getPositioningdurationminutes();
              // Don't set positioningAirportCode since we don't have positioningFromAirportId in OrderRequestPlaneEntity
              const positioningAirportCode: string | undefined = undefined;
              
              // Если есть flightDurationMinutes, пересчитываем routeDateTo
              let finalRouteDateTo = routeDateTo || routeDateFrom!;
              if (planeEntity.getFlightdurationminutes() && routeDateFrom) {
                const airportFrom = firstRoute!.getAirportfrom()!;
                const airportTo = lastRoute!.getAirportto()!;
                const airportFromMapped = mapAirportEntityToIAirport(airportFrom);
                const airportToMapped = mapAirportEntityToIAirport(airportTo);
                finalRouteDateTo = calculateArrivalDate(
                  airportFromMapped,
                  airportToMapped,
                  routeDateFrom,
                  planeEntity.getFlightdurationminutes()
                );
              }
              
              // Determine the currency to use for display
              let displayCurrency: ICurrency | null = null;
              if (finalCurrency) {
                const currencyId = finalCurrency.getCurrencyid();
                const currencyName = finalCurrency.getName();
                // Always set currency if we have a currency object with an ID
                // Ensure we have a currency name - if empty, try to get from orderRequestCurrency as fallback
                let finalCurrencyName = currencyName || '';
                if (!finalCurrencyName && orderRequestCurrencyRaw) {
                  finalCurrencyName = orderRequestCurrencyRaw.getName() || '';
                }
                displayCurrency = {
                  id: currencyId || 0,
                  name: finalCurrencyName,
                };
              } else if (orderRequestCurrencyRaw) {
                // Fallback: use orderRequest currency if finalCurrency is not available
                displayCurrency = {
                  id: orderRequestCurrencyRaw.getCurrencyid(),
                  name: orderRequestCurrencyRaw.getName() || '',
                };
              }
              
              // Extract plane type and category information
              const planeType = plane.getPlanetype();
              const planeTypeName = planeType?.getName() || undefined;
              const planeCategory = planeType?.getPlanecategory();
              const planeCategoryName = planeCategory?.getName() || undefined;
              
              allResponses.push({
                id: planeEntity.getOrderrequestplaneid(), // Используем orderRequestPlaneId как уникальный ID
                planeRegistrationNumber: plane.getRegistrationnumber(),
                planeId: plane.getPlaneid(),
                planeTypeName,
                planeCategoryName,
                routeDateFrom: routeDateFrom!,
                routeDateTo: finalRouteDateTo,
                price,
                currency: displayCurrency,
                orderRequestCurrency: orderRequestCurrencyRaw ? {
                  id: orderRequestCurrencyRaw.getCurrencyid(),
                  name: orderRequestCurrencyRaw.getName() || '',
                } : null,
                priceInUserCurrency,
                userCurrency: userCurrencyObj,
                passengers: passengers || 0,
                refuelings,
                isAccepted,
                isDeclined,
                isPending,
                similarOrderId: orderEntity.getOrderid(),
                similarOrderStatusId: orderEntity.getOrderstatusid?.() || undefined,
                positioningDurationMinutes: positioningDurationMinutes && positioningDurationMinutes > 0 ? positioningDurationMinutes : undefined,
                positioningAirportCode,
              });
            });
          });
        });
        
        setResponses(sortPastResponses(allResponses));
      }
    } catch (error) {
      Logger.error(`Failed to load similar list: ${error}`);
      setResponses([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatRouteDate = (dateFrom: Date, dateTo: Date): string => {
    const fromFormatted = formatDate(dateFrom, offset, "datetime_short_year");
    
    // Check if dates are on the same day (ignore time)
    const fromDateOnly = new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate());
    const toDateOnly = new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate());
    const isSameDay = fromDateOnly.getTime() === toDateOnly.getTime();
    
    if (isSameDay) {
      // If same day, show departure date/time and arrival time only
      const toTimeFormatted = formatDate(dateTo, offset, "time");
      return `${fromFormatted} – ${toTimeFormatted}`;
    } else {
      // If different days, show both dates
      const toFormatted = formatDate(dateTo, offset, "datetime_short_year");
      return `${fromFormatted} – ${toFormatted}`;
    }
  };

  return (
    <StyledModal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      centered
    >
      <ModalHeader>
        <HeaderLeft>
          <HeaderTitle>{intl.formatMessage(PastResponsesModalMessages.title)}</HeaderTitle>
          {routeDisplay && (
            <RouteInfo>
              <RouteText>{routeDisplay}</RouteText>
            </RouteInfo>
          )}
        </HeaderLeft>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </ModalHeader>

      <ModalContent>
        {isLoading ? (
          <LoadingContainer>
            <Loader />
          </LoadingContainer>
        ) : responses.length === 0 ? (
          <EmptyContainer>
            <NoData 
              title={intl.formatMessage(PastResponsesModalMessages.noSimilarRequests)} 
            />
          </EmptyContainer>
        ) : (
          <ResponsesContainer>
            {responses.map((response, index) => (
              <ResponseCard key={`${response.similarOrderId ?? 'r'}-${response.id}-${response.routeDateFrom.getTime()}-${index}`} $isLast={index === responses.length - 1}>
              <ResponseCell $minWidth={50}>
                <CellLabel>{intl.formatMessage(PastResponsesModalMessages.plane)}</CellLabel>
                <CellContent>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CellValue>{response.planeRegistrationNumber}</CellValue>
                    {response.planeTypeName || response.planeCategoryName ? (
                      <Tooltip 
                        title={
                          <div>
                            {response.planeCategoryName && <div>Class: {response.planeCategoryName}</div>}
                            {response.planeTypeName && <div>Model: {response.planeTypeName}</div>}
                          </div>
                        }
                      >
                        <InfoIconWrapper style={{ cursor: 'help' }}>
                          <InfoIcon />
                        </InfoIconWrapper>
                      </Tooltip>
                    ) : null}
                  </div>
                </CellContent>
              </ResponseCell>

              <ResponseCell $minWidth={70}>
                <CellLabel>{intl.formatMessage(PastResponsesModalMessages.routeDate)}</CellLabel>
                <CellContent>
                  <CellValue>{formatRouteDate(response.routeDateFrom, response.routeDateTo)}</CellValue>
                </CellContent>
              </ResponseCell>

              <PriceCell>
                <CellLabel>{intl.formatMessage(PastResponsesModalMessages.price)}</CellLabel>
                <CellContent>
                  <PriceContent>
                    <div>
                      <PriceValue>
                        {response.priceInUserCurrency && response.userCurrency
                          ? DisplayFlyPrice(response.priceInUserCurrency, response.userCurrency)
                          : DisplayFlyPrice(response.price, getPriceDisplayCurrency(response))}
                        {response.isPending && (
                          <Tooltip title={(() => {
                            let tooltipText = intl.formatMessage(PastResponsesModalMessages.pendingResponse);
                            if (response.positioningDurationMinutes && response.positioningDurationMinutes > 0) {
                              const hours = Math.floor(response.positioningDurationMinutes / 60);
                              const minutes = response.positioningDurationMinutes % 60;
                              const durationStr = `${hours}:${String(minutes).padStart(2, '0')}`;
                              if (response.positioningAirportCode) {
                                tooltipText += ` (includes positioning ${durationStr} ${response.positioningAirportCode})`;
                              } else {
                                tooltipText += ` (includes positioning ${durationStr})`;
                              }
                            }
                            return tooltipText;
                          })()}>
                            <PendingIconWrapper>
                              <WarningIcon />
                            </PendingIconWrapper>
                          </Tooltip>
                        )}
                      </PriceValue>
                      {response.priceInUserCurrency && response.userCurrency && response.orderRequestCurrency && 
                       Number(response.userCurrency.id) !== Number(response.orderRequestCurrency.id) && (
                        <PriceInUserCurrency>
                          {DisplayFlyPrice(response.price, response.orderRequestCurrency)}
                        </PriceInUserCurrency>
                      )}
                    </div>
                  </PriceContent>
                </CellContent>
              </PriceCell>

              <ResponseCell $minWidth={50}>
                <CellLabel>{intl.formatMessage(PastResponsesModalMessages.passengers)}</CellLabel>
                <CellContent>
                  <IconValueWrapper>
                    <PassengerIconWrapper>
                      <PaxIcon />
                    </PassengerIconWrapper>
                    <CellValue>{response.passengers}</CellValue>
                  </IconValueWrapper>
                </CellContent>
              </ResponseCell>

              <ResponseCell $minWidth={70}>
                <CellLabel>{intl.formatMessage(PastResponsesModalMessages.refuelings)}</CellLabel>
                <CellContent>
                  <IconValueWrapper>
                    <FuelIconWrapper>
                      <FuelIcon />
                    </FuelIconWrapper>
                    <RefuelValue>{response.refuelings}</RefuelValue>
                  </IconValueWrapper>
                </CellContent>
              </ResponseCell>

              <ActionsCell>
                <ActionsButtons>
                  <AcceptButton
                    active={response.isAccepted}
                    disabled={true}
                    onClick={() => {
                      // Кнопка заблокирована, нельзя изменить статус
                    }}
                  />
                  <DeclineButton
                    active={response.isDeclined}
                    disabled={true}
                    onClick={() => {
                      // Кнопка заблокирована, нельзя изменить статус
                    }}
                  />
                </ActionsButtons>
                {(() => {
                  const statusId = response.similarOrderStatusId;
                  const statusText =
                    statusId === OrderStatusEnum.NOT_ANSWERED
                      ? intl.formatMessage(PastResponsesModalMessages.orderStatusNotAnswered)
                      : statusId === OrderStatusEnum.ACCEPTED
                        ? intl.formatMessage(PastResponsesModalMessages.orderStatusAccepted)
                        : statusId === OrderStatusEnum.DECLINED
                          ? intl.formatMessage(PastResponsesModalMessages.orderStatusDeclined)
                          : null;
                  return statusText ? <OrderStatusLabel>{statusText}</OrderStatusLabel> : null;
                })()}
              </ActionsCell>
            </ResponseCard>
            ))}
          </ResponsesContainer>
        )}
      </ModalContent>
    </StyledModal>
  );
};
